import React, { ReactNode, Ref, useRef, forwardRef, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import Avatar, { AvatarProps } from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grid from '@mui/material/Grid2';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
// import ListSubheader from '@mui/material/ListSubheader';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
// import Typography from '@mui/material/Typography';

// project imports
import { ThemeDirection, ThemeMode } from 'config';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import { drawerWidth, gridSpacing } from 'store/constant';
import useConfig from 'hooks/useConfig';

// assets
//? import Banner from './Banner';
import { IconCategory } from '@tabler/icons-react';

//* Navigation icons
import { NavLink, useLocation } from 'react-router';
// import useLocalization from 'hooks/useLocalization';
import { useSelector } from 'store';
import { FormattedMessage } from 'react-intl';
import { ellipsisTextOverflowFewRowsStyles } from 'themes/common/typography';
import pages from 'menu-items/pages';
// import { NavItemType } from 'types';
import { getNestedValue } from 'utils/helpers';
import useLocalization from 'hooks/useLocalization';

interface HeaderAvatarProps extends AvatarProps {
  children: ReactNode;
}

function HeaderAvatarComponent({ children, sx, ...others }: HeaderAvatarProps, ref: Ref<HTMLDivElement>) {
  const theme = useTheme();

  return (
    <Avatar
      ref={ref}
      sx={{
        ...theme.typography.commonAvatar,
        ...theme.typography.mediumAvatar,
        display: 'flex',
        bgcolor: theme.palette.mode === ThemeMode.DARK ? 'dark.main' : 'secondary.light',
        color: theme.palette.mode === ThemeMode.DARK ? 'secondary.main' : 'secondary.dark',
        '&:hover': {
          bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.main' : 'secondary.dark',
          color: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.light',
        },
      }}
      {...others}
    >
      {children}
    </Avatar>
  );
}

const HeaderAvatar = forwardRef(HeaderAvatarComponent);

export default function MegaMenuSection() {
  const theme = useTheme();
  const location = useLocation();
  const { themeDirection } = useConfig();
  const { currentLocale } = useLocalization();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // const localization = React.useMemo(() => currentLocale.settingsPopup.navigation, [currentLocale]);

  const user = useSelector((state) => state.user);
  const company = useSelector((state) => state.company);

  const menuItemsVisibility = React.useMemo<{
    [key: string]: boolean;
  }>(() => {
    const visibility: { [key: string]: boolean } = {
      'favourite-messages': true,
      contacts: true,
      templates: true,
      customization: true,
      'user-profile': true,
      'company-settings': true,
      'app-users': user?.Role === 'admin',
      labels: true,
      drive: true,
      'broadcasts-lists': true,
      broadcasts: true,
      'mail-whatsapp': true,
      workflows: user?.Role === 'admin',
      AI: false,
      AISupport: true,
      webhooks: true,
      fireberrySync: company?.CompanyType === 'fireberry',
      prioritySync: company?.CompanyType === 'priority',
      kanban: true,
      calendar: true,
      'docs-sign': true,
      extension: true,
      'data-export': true,
      statistics: true,
    };
    return visibility;
  }, [company?.CompanyId, company?.CompanyType, user?.Role]);

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<any>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (
    event:
      | React.MouseEvent<Document, MouseEvent>
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>
      | MouseEvent
      | TouchEvent,
  ) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  // For 3 sections: get visible children for each section (skip section title, only items)
  const getVisibleSectionItems = (section: any) =>
    section.children?.filter((item: any) => item.id && (menuItemsVisibility[item.id] ?? true)) || [];

  const visibleSections = (pages.children || []).map((section) => getVisibleSectionItems(section));

  return (
    <>
      <HeaderAvatar
        variant="rounded"
        ref={anchorRef}
        aria-controls={open ? 'menu-list-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <IconCategory stroke={1.5} size="20px" />
      </HeaderAvatar>
      <Popper
        placement="bottom-start"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        sx={{
          ...(themeDirection === ThemeDirection.RTL && {}),
        }}
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 20],
            },
          },
        ]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper
                sx={{
                  width: isMobile ? '100vw' : {
                    md: `calc(100vw - 100px)`,
                    lg: `calc(100vw - ${drawerWidth + 100}px)`,
                    xl: `calc(100vw - ${drawerWidth + 140}px)`,
                  },
                  maxWidth: isMobile ? 'none' : { xl: 900, md: 764 },
                  maxHeight: isMobile ? '80vh' : 'none',
                  overflowY: isMobile ? 'auto' : 'visible',
                }}
              >
                {open && (
                  <MainCard
                    border={false}
                    elevation={16}
                    content={false}
                    boxShadow
                    shadow={theme.shadows[16]}
                    sx={{ overflow: { p: 1, xs: 'visible', md: 'hidden' } }}
                  >
                    {/* <Grid container spacing={gridSpacing}>
                      <Grid size={{ md: 4 }}>
                        <Banner />
                      </Grid>
                      <Grid size={{ md: 8 }}> */}
                    <Grid
                      container
                      spacing={isMobile ? 0 : gridSpacing}
                      direction={isMobile ? 'column' : 'row'}
                      sx={{
                        // pt: 3,
                        '& .MuiListItemButton-root:hover': {
                          bgcolor: 'transparent',
                          '& .MuiTypography-root': {
                            color: 'secondary.main',
                          },
                        },
                        '& .MuiListItemIcon-root': {
                          minWidth: 16,
                        },
                      }}
                    >
                      {/* {menuBlocks.map((menuBlock, index) => (
                        <Grid key={index} size={4}>
                          <List
                            component="nav"
                            aria-labelledby={`list-${menuBlock.name}`}
                            subheader={
                              menuBlock.label && (
                                <ListSubheader id={`list-${menuBlock.name}`}>
                                  <Typography variant="subtitle1">{menuBlock.label}</Typography>
                                </ListSubheader>
                              )
                            }
                          > */}
                      {/* {menuBlock.menuItems.map((menuItem, index) => { */}
                      {/* Render each section in a separate row */}
                      {/* Render sections side by side */}
                      {visibleSections.map((sectionItems, sectionIdx) => (
                        <div 
                          style={{ 
                            flex: isMobile ? 'none' : 1, 
                            minWidth: 0,
                            width: isMobile ? '100%' : 'auto'
                          }} 
                          key={sectionIdx}
                        >
                          <List
                            component="nav"
                            subheader={
                              pages.children?.[sectionIdx]?.title ? (
                                <ListItemText
                                  primary={
                                    pages.children?.[sectionIdx]?.customPage ? (
                                      getNestedValue(currentLocale, pages.children?.[sectionIdx]?.title)
                                    ) : (
                                      <FormattedMessage id={pages.children?.[sectionIdx]?.title} />
                                    )
                                  }
                                  sx={{
                                    pl: isMobile ? 1.5 : 2,
                                    pt: isMobile ? 1.5 : 2,
                                    '& .MuiListItemText-primary': {
                                      fontSize: isMobile ? 18 : 16,
                                      fontWeight: 700,
                                      color: 'grey.800',
                                    },
                                  }}
                                />
                              ) : null
                            }
                          >
                            {sectionItems.map((menuItem: any) => (
                              <ListItemButton
                                key={menuItem.id}
                                component={NavLink}
                                to={menuItem.url || ''}
                                selected={location.pathname.includes(menuItem.url || '')}
                                onClick={handleClose}
                                sx={{
                                  gap: isMobile ? 1 : 1.5,
                                  alignItems: 'flex-start',
                                  height: isMobile ? 64 : 88,
                                  py: isMobile ? 1 : 1.5,
                                  px: isMobile ? 1.5 : 2,
                                  '&:hover': {
                                    '& .MuiListItemIcon-root': {
                                      color: theme.palette.secondary.main,
                                    },
                                  },
                                }}
                              >
                                {menuItem.icon && (
                                  <ListItemIcon
                                    sx={{
                                      p: isMobile ? 1 : 1.25,
                                      borderRadius: 2,
                                      bgcolor: (theme) => theme.palette.background.paper,
                                      color: (theme) => theme.palette.primary.main,
                                      minWidth: isMobile ? 40 : 48,
                                      width: isMobile ? 40 : 48,
                                      height: isMobile ? 40 : 48,
                                    }}
                                  >
                                    {menuItem.icon}
                                  </ListItemIcon>
                                )}
                                <ListItemText
                                  primary={
                                    menuItem.title ? (
                                      menuItem.customPage ? (
                                        getNestedValue(currentLocale, menuItem.title)
                                      ) : (
                                        <FormattedMessage id={menuItem.title} />
                                      )
                                    ) : undefined
                                  }
                                  secondary={
                                    menuItem.caption ? (
                                      menuItem.customPage ? (
                                        getNestedValue(currentLocale, menuItem.caption)
                                      ) : (
                                        <FormattedMessage id={menuItem.caption} />
                                      )
                                    ) : undefined
                                  }
                                  sx={{
                                    '& .MuiListItemText-primary': {
                                      fontSize: isMobile ? 16 : 14,
                                      color: 'grey.900',
                                      fontWeight: 700,
                                      lineHeight: 1.43,
                                    },
                                    '& .MuiListItemText-secondary': {
                                      ...ellipsisTextOverflowFewRowsStyles(2),
                                      fontSize: isMobile ? 14 : 12,
                                      color: 'grey.600',
                                      fontWeight: 400,
                                      lineHeight: 1.5,
                                    },
                                  }}
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        </div>
                      ))}
                      {/* })}
                      </List>
                        </Grid>
                      ))} */}
                    </Grid>
                    {/* </Grid>
                    </Grid> */}
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
